import { defineStore } from 'pinia';

export interface ColorbarInfo {
  colormap: string;
  minValue: number;
  maxValue: number;
  maskType?: string;
}

// Main image object interface
export interface ClipboardImage {
  screenshotUID: string;
  dateTaken: string;
  title: string;
  description: string;
  imageBlob?: Blob;
  imageDataURL: string;
  thumbnailDataURL: string;
  viewportId?: string | null;
  seriesIdx?: number | null;
  sliceIdx?: number | null;
  imageIdx?: number | null;
  tags: string[];
  width?: number | null;
  height?: number | null;
  colorbarInfo?: ColorbarInfo | null;
}

// State interface
interface ImageClipboardState {
  images: ClipboardImage[];
  selectedImageId: string | null;
}

function generatescreenshotUID(): string {
  return `${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
}

export const useImageClipboardStore = defineStore('image-clipboard-store', {
  state: (): ImageClipboardState => ({
    images: [],
    selectedImageId: null,
  }),

  getters: {
    getImages: (state: ImageClipboardState): ClipboardImage[] => state.images,

    getImageById:
      (state: ImageClipboardState) =>
        (imageId: string): ClipboardImage | undefined => {
          return state.images.find(img => img.screenshotUID === imageId);
        },

    getSelectedImage: (state: ImageClipboardState): ClipboardImage | null => {
      if (!(state.selectedImageId ?? '')) {
        return state.images.length > 0 ? state.images[0] : null;
      }
      return (
        state.images.find(
          img => img.screenshotUID === state.selectedImageId,
        ) || null
      );
    },

    getMostRecentImage: (state: ImageClipboardState): ClipboardImage | null => {
      if (state.images.length === 0) return null;
      return state.images.reduce((latest, current) =>
        new Date(current.dateTaken) > new Date(latest.dateTaken)
          ? current
          : latest,
      );
    },

    getImagesCount: (state: ImageClipboardState): number => state.images.length,

    getImagesSortedByDate: (state: ImageClipboardState): ClipboardImage[] => {
      return [...state.images].sort(
        (a, b) =>
          new Date(b.dateTaken).getTime() - new Date(a.dateTaken).getTime(),
      );
    },
  },

  actions: {
    addImage(
      this: ImageClipboardState,
      imageData: Partial<ClipboardImage>,
    ): string {
      const newImage: ClipboardImage = {
        screenshotUID: generatescreenshotUID(),
        dateTaken: new Date().toISOString(),
        title: imageData.title || `Screenshot ${this.images.length + 1}`,
        description: imageData.description || '',
        imageBlob: imageData.imageBlob,
        imageDataURL: imageData.imageDataURL!,
        thumbnailDataURL: imageData.thumbnailDataURL || imageData.imageDataURL!,
        viewportId: imageData.viewportId || null,
        seriesIdx: imageData.seriesIdx || null,
        sliceIdx: imageData.sliceIdx || null,
        imageIdx: imageData.imageIdx || null,
        tags: imageData.tags || [],
        width: imageData.width || null,
        height: imageData.height || null,
        colorbarInfo: imageData.colorbarInfo || null,
      };

      this.images.unshift(newImage); // Add to beginning (most recent first)

      // Set as selected if it's the first image or if no image is currently selected
      if (this.images.length === 1 || !this.selectedImageId) {
        this.selectedImageId = newImage.screenshotUID;
      }

      return newImage.screenshotUID;
    },

    deleteImage(this: ImageClipboardState, screenshotUID: string): boolean {
      const imageIndex = this.images.findIndex(
        img => img.screenshotUID === screenshotUID,
      );
      if (imageIndex === -1) return false;

      const wasSelected = this.selectedImageId === screenshotUID;
      this.images.splice(imageIndex, 1);

      // If the deleted image was selected, select another one
      if (wasSelected) {
        if (this.images.length > 0) {
          // Select the image at the same index, or the last one if index is out of bounds
          const newIndex = Math.min(imageIndex, this.images.length - 1);
          this.selectedImageId = this.images[newIndex]?.screenshotUID || null;
        }
        else {
          this.selectedImageId = null;
        }
      }

      return true;
    },

    deleteAllImages(this: ImageClipboardState): void {
      this.images = [];
      this.selectedImageId = null;
    },

    selectImage(this: ImageClipboardState, screenshotUID: string): boolean {
      const image = this.images.find(
        img => img.screenshotUID === screenshotUID,
      );
      if (image) {
        this.selectedImageId = screenshotUID;
        return true;
      }
      return false;
    },

    updateImageInfo(
      this: ImageClipboardState,
      screenshotUID: string,
      updates: Partial<ClipboardImage>,
    ): boolean {
      const image = this.images.find(
        img => img.screenshotUID === screenshotUID,
      );
      if (image) {
        Object.assign(image, updates);
        return true;
      }
      return false;
    },

    addTagToImage(
      this: ImageClipboardState,
      screenshotUID: string,
      tag: string,
    ): boolean {
      const image = this.images.find(
        img => img.screenshotUID === screenshotUID,
      );
      if (image && !image.tags.includes(tag)) {
        image.tags.push(tag);
        return true;
      }
      return false;
    },

    removeTagFromImage(
      this: ImageClipboardState,
      screenshotUID: string,
      tag: string,
    ): boolean {
      const image = this.images.find(
        img => img.screenshotUID === screenshotUID,
      );
      if (image) {
        const tagIndex = image.tags.indexOf(tag);
        if (tagIndex !== -1) {
          image.tags.splice(tagIndex, 1);
          return true;
        }
      }
      return false;
    },

    exportImages(this: ImageClipboardState): ClipboardImage[] {
      return this.images.map(img => ({
        ...img,
        // Convert blob to data URL for export if needed
        imageDataURL: img.imageDataURL,
        thumbnailDataURL: img.thumbnailDataURL,
      }));
    },

    importImages(
      this: ImageClipboardState,
      imagesData: ClipboardImage[],
    ): void {
      // Validate and import images
      const validImages = imagesData.filter(
        img => img.screenshotUID && (img.imageDataURL || img.imageBlob),
      );

      this.images = [...this.images, ...validImages];

      if (this.images.length > 0 && !this.selectedImageId) {
        this.selectedImageId = this.images[0].screenshotUID;
      }
    },
  },
});
